/*
 * Shared plumbing for the quotes / invoices / clients services: the contract's query format, the
 * Idempotency-Key header and error parsing. The real client and the dev mock both go through the
 * same axios instance, so this code runs unchanged in either mode.
 */
import type { AxiosRequestConfig } from 'axios';
import { serializeQuery } from '@/features/quotes/lib/filters';
import type { Envelope } from '@/types/quotes';
import { subscriptionRequiredRefusal } from '@/lib/subscription-error-handler';

export const ADMIN = '/v1/admin';

export const queryConfig = (params?: object): AxiosRequestConfig => ({
  params,
  paramsSerializer: { serialize: serializeQuery },
});

export function newIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  // Very old browsers: a random v4-shaped id is enough, the server only needs uniqueness per request.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

export const idempotent = (key: string | undefined): AxiosRequestConfig => ({
  headers: { 'Idempotency-Key': key ?? newIdempotencyKey() },
});

export const unwrap = <T>(res: { data: Envelope<T> }): T => res.data.data;

export interface ParsedApiError {
  /** No response at all: offline, DNS, timeout, CORS. Safe to retry with the same Idempotency-Key. */
  network: boolean;
  status: number | null;
  /** Machine code from the contract's error catalogue (version_conflict, quote_locked, ...). */
  code: string | null;
  message: string;
  /** Field -> machine code, from 422 validation errors. */
  fields: Record<string, string>;
  /** The stored resource that came with a 409 version_conflict. */
  current: unknown;
  /** 5xx and 429: worth retrying. */
  retryable: boolean;
  /** 429: how long to wait before trying again, from `retry_after_seconds` or the Retry-After header. */
  retryAfterSeconds?: number;
  /** 403 subscription_required: why the account is read-only (trial_ended, payment_lapsed, cancelled). */
  reason?: string;
}

function retryAfter(data: { retry_after_seconds?: unknown }, res: { headers?: Record<string, unknown> }): number | undefined {
  const body = Number(data.retry_after_seconds);
  if (Number.isFinite(body) && body > 0) return body;
  const header = Number(res.headers?.['retry-after'] ?? res.headers?.['Retry-After']);
  return Number.isFinite(header) && header > 0 ? header : undefined;
}

export function parseApiError(error: unknown): ParsedApiError {
  const e = error as { response?: { status?: number; data?: any; headers?: Record<string, unknown> }; message?: string; code?: string } | undefined;
  const res = e?.response;
  if (!res) {
    return { network: true, status: null, code: null, message: e?.message ?? 'Network error', fields: {}, current: undefined, retryable: true };
  }
  const data = res.data ?? {};
  const status = res.status ?? 0;
  // A read-only refusal carries its user-facing sentence in errors.message; `error` is only the title.
  const refusal = subscriptionRequiredRefusal(error);
  const message =
    refusal?.message ||
    (typeof data.error === 'string' && data.error) ||
    (typeof data.message === 'string' && data.message) ||
    (Array.isArray(data.errors) && typeof data.errors[0] === 'string' && data.errors[0]) ||
    'Request failed';
  return {
    network: false,
    status,
    code: typeof data.code === 'string' ? data.code : null,
    message,
    fields: data.fields && typeof data.fields === 'object' ? data.fields : {},
    current: data.current,
    retryable: status >= 500 || status === 429,
    ...(retryAfter(data, res) !== undefined ? { retryAfterSeconds: retryAfter(data, res) } : {}),
    ...(refusal ? { reason: refusal.reason } : {}),
  };
}

/**
 * The contract's `status_counts` has exactly one integer per status and nothing else; the list screens also want
 * `all` (the empty-state test) and `needs_you` (a filter option), so they are derived here, once, from the same
 * response. A server that already sends them (the dev mock does) is left alone.
 */
export function withDerivedCounts<C extends Record<string, number | undefined>>(counts: C | undefined, needsYou?: number): C & { all: number; needs_you?: number } {
  const c = { ...(counts ?? {}) } as Record<string, number | undefined>;
  const all = c.all ?? Object.entries(c).reduce((sum, [k, v]) => (k === 'needs_you' ? sum : sum + (v ?? 0)), 0);
  return { ...c, all, needs_you: c.needs_you ?? needsYou } as C & { all: number; needs_you?: number };
}
